import { useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Cpu,
  Activity,
  ChevronLeft,
  ChevronRight,
  Users,
  CheckCircle,
  Film,
  CreditCard,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type ModelRow = {
  model: string
  estimatedCostCents: number
  generations: number
  succeeded: number
  failed: number
  totalDurationSec: number
}
type ModeRow = {
  mode: string
  estimatedCostCents: number
  generations: number
}
type PlanRow = {
  plan: string
  estimatedCostCents: number
  generations: number
  users: number
}
type UserRow = {
  userId: string
  email: string
  name?: string
  estimatedCostCents: number
  generations: number
}
type DailyRow = {
  date: string
  estimatedCostCents: number
  generations: number
  succeeded: number
  failed: number
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function formatDuration(seconds: number): string {
  if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}h`
  if (seconds >= 60) return `${(seconds / 60).toFixed(1)}m`
  return `${Math.round(seconds)}s`
}

function todayString() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function FalDashboard() {
  const [date, setDate] = useState(todayString)
  const data = useQuery(api.falUsage.getDashboard, { date })
  const [userPage, setUserPage] = useState(0)
  const [prevDate, setPrevDate] = useState(date)
  if (date !== prevDate) {
    setPrevDate(date)
    setUserPage(0)
  }

  const isToday = date === todayString()

  const isLoading = data === undefined
  const hasData = data != null

  const USERS_PER_PAGE = 25
  const totalUsers = hasData && data.byUser ? data.byUser.length : 0
  const totalUserPages = Math.ceil(totalUsers / USERS_PER_PAGE)
  const pagedUsers = hasData && data.byUser
    ? data.byUser.slice(userPage * USERS_PER_PAGE, (userPage + 1) * USERS_PER_PAGE)
    : []

  return (
    <div className="space-y-6">
      {/* Day nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setDate(shiftDate(date, -1))}
          className="p-1.5 rounded hover:bg-neutral-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xl font-bold text-blue-900 border border-blue-200 px-4 py-2 rounded-md bg-blue-50 min-w-[180px] text-center">
          {hasData ? data.month : '...'}
        </span>
        <button
          onClick={() => setDate(shiftDate(date, 1))}
          disabled={isToday}
          className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Estimated Cost</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {isLoading ? '...' : formatCurrency(data.totals.estimatedCostCents)}
                </p>
              </div>
              <Cpu className="h-5 w-5 text-purple-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Total Generations</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {isLoading ? '...' : data.totals.generations.toLocaleString()}
                </p>
              </div>
              <Film className="h-5 w-5 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Success Rate</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {isLoading ? '...' : `${data.totals.successRate.toFixed(1)}%`}
                </p>
              </div>
              <CheckCircle className="h-5 w-5 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500">Total Duration</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {isLoading ? '...' : formatDuration(data.totals.totalDurationSec)}
                </p>
              </div>
              <Activity className="h-5 w-5 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Model */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="h-4 w-4" />
              Cost by Model
            </CardTitle>
            <CardDescription>Estimated cost by video generation model</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-300">Loading...</span>
                      <span className="text-sm text-neutral-300">--</span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full" />
                  </div>
                ))}
              </div>
            ) : data.byModel.length === 0 ? (
              <p className="text-sm text-neutral-400 py-8 text-center">No usage data yet</p>
            ) : (
              <div className="space-y-4">
                {data.byModel.map((item: ModelRow) => (
                  <div key={item.model}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-700">{item.model}</span>
                      <span className="text-sm font-medium text-neutral-900">
                        <span className="text-xs text-neutral-400 mr-1">
                          ({item.generations} · {item.succeeded}/{item.failed})
                        </span>
                        {formatCurrency(item.estimatedCostCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-purple-500"
                        style={{
                          width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {hasData && data.byModel.length > 0 && (
              <div className="mt-6 pt-4 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">Total</span>
                  <span className="text-sm font-bold text-neutral-900">
                    {formatCurrency(data.totals.estimatedCostCents)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost by Generation Mode */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Cost by Generation Mode
            </CardTitle>
            <CardDescription>Text-to-video vs image-to-video, etc.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-300">Loading...</span>
                      <span className="text-sm text-neutral-300">--</span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full" />
                  </div>
                ))}
              </div>
            ) : data.byMode.length === 0 ? (
              <p className="text-sm text-neutral-400 py-8 text-center">No usage data yet</p>
            ) : (
              <div className="space-y-4">
                {data.byMode.map((item: ModeRow) => (
                  <div key={item.mode}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-neutral-700">{item.mode}</span>
                      <span className="text-sm font-medium text-neutral-900">
                        <span className="text-xs text-neutral-400 mr-1">({item.generations})</span>
                        {formatCurrency(item.estimatedCostCents)}
                      </span>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500"
                        style={{
                          width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost by Plan */}
      {hasData && data.byPlan && data.byPlan.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Cost by Plan
            </CardTitle>
            <CardDescription>Usage split by user's effective subscription plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.byPlan.map((item: PlanRow) => (
                <div key={item.plan}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-neutral-700 capitalize">{item.plan}</span>
                    <span className="text-sm font-medium text-neutral-900">
                      <span className="text-xs text-neutral-400 mr-1">
                        ({item.generations} gens · {item.users} {item.users === 1 ? 'user' : 'users'})
                      </span>
                      {formatCurrency(item.estimatedCostCents)}
                    </span>
                  </div>
                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-amber-500"
                      style={{
                        width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost by User */}
      {hasData && data.byUser && data.byUser.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Cost by User
            </CardTitle>
            <CardDescription>Top users by estimated cost ({totalUsers} users)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pagedUsers.map((item: UserRow) => (
                <div key={item.userId}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-neutral-700">
                      {item.name ? `${item.name} (${item.email})` : item.email}
                    </span>
                    <span className="text-sm font-medium text-neutral-900">
                      <span className="text-xs text-neutral-400 mr-1">({item.generations})</span>
                      {formatCurrency(item.estimatedCostCents)}
                    </span>
                  </div>
                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-green-500"
                      style={{
                        width: `${data.totals.estimatedCostCents > 0 ? Math.min((item.estimatedCostCents / data.totals.estimatedCostCents) * 100, 100) : 0}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {totalUserPages > 1 && (
              <div className="mt-4 pt-3 border-t border-neutral-200 flex items-center justify-between">
                <span className="text-xs text-neutral-500">
                  {userPage * USERS_PER_PAGE + 1}–{Math.min((userPage + 1) * USERS_PER_PAGE, totalUsers)} of {totalUsers}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setUserPage(userPage - 1)}
                    disabled={userPage === 0}
                    className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-neutral-500 px-2">
                    {userPage + 1} / {totalUserPages}
                  </span>
                  <button
                    onClick={() => setUserPage(userPage + 1)}
                    disabled={userPage >= totalUserPages - 1}
                    className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Daily Trend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Daily Trend
          </CardTitle>
          <CardDescription>Cost and generations by day</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 flex items-center justify-center text-neutral-300">Loading...</div>
          ) : data.daily.length === 0 ? (
            <p className="text-sm text-neutral-400 py-8 text-center">No usage data yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 font-medium text-neutral-500">Date</th>
                    <th className="text-right py-2 font-medium text-neutral-500">Cost</th>
                    <th className="text-right py-2 font-medium text-neutral-500">Gens</th>
                    <th className="text-right py-2 font-medium text-neutral-500">OK/Fail</th>
                    <th className="py-2 pl-4 font-medium text-neutral-500 w-48"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((day: DailyRow) => {
                    const maxCost = Math.max(...data.daily.map((d: DailyRow) => d.estimatedCostCents))
                    return (
                      <tr key={day.date} className="border-b border-neutral-100">
                        <td className="py-2 text-neutral-700">
                          {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="py-2 text-right font-medium text-neutral-900">
                          {formatCurrency(day.estimatedCostCents)}
                        </td>
                        <td className="py-2 text-right text-neutral-600">
                          {day.generations}
                        </td>
                        <td className="py-2 text-right text-neutral-600">
                          <span className="text-green-600">{day.succeeded}</span>
                          {' / '}
                          <span className={day.failed > 0 ? 'text-red-500' : 'text-neutral-400'}>{day.failed}</span>
                        </td>
                        <td className="py-2 pl-4">
                          <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                            <div
                              className={cn('h-full rounded-full bg-purple-400')}
                              style={{ width: `${maxCost > 0 ? (day.estimatedCostCents / maxCost) * 100 : 0}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
